import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { RunStatus } from '../../../../../../../../../backend/src/types/types';
import { RunDetailModal } from '../components/modals/run-detail-modal';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useUpdateRun } from '@/api/mutations/useUpdateRun';
import { useGetMatterRuns } from '@/api/queries/useGetMatterRuns';
import { MatterRun } from '@/api/types/backend-dto-types';
import formatDate, { formatDuration } from '@/helpers/formatDate';
import { getRunType } from '@/helpers/runs';
import { Chip } from '@/new/components/chip';
import { runNameSchema, RunNameFormData } from '@/new/components/dialogs/run-name-schema';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/new/components/shadcn/form';
import { Input } from '@/new/components/shadcn/input';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import {
  TableBody,
  TableCell,
  Table as TableComponent,
  TableHead,
  TableHeader,
  TableRow,
} from '@/new/components/shadcn/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { TableNullField } from '@/new/components/table/table-null-field';
import { TablePagination } from '@/new/components/table/table-pagination';

const getRunStatusChip = (run: MatterRun) => {
  if (run.status === RunStatus.AwaitingApproval) {
    return <Chip className="bg-amber-50 text-amber-700 ring-1 ring-amber-200 ring-inset">Awaiting Approval</Chip>;
  }
  if (run.status === RunStatus.Failed) {
    return <Chip className="bg-red-50 text-red-700 ring-1 ring-red-200 ring-inset">Failed</Chip>;
  }
  if (run.status === RunStatus.Cancelled) {
    return <Chip className="bg-amber-50 text-amber-700 ring-1 ring-amber-200 ring-inset">Cancelled</Chip>;
  }
  if (run.status === RunStatus.ApprovalTimeout) {
    return <Chip className="bg-amber-50 text-amber-700 ring-1 ring-amber-200 ring-inset">Approval Timeout</Chip>;
  }
  if (run.completedAt) {
    const status = run.status?.toLowerCase() || '';
    if (
      status.includes('fail') ||
      (run.failedPagesCount && run.failedPagesCount > 0 && run.totalPages === run.failedPagesCount)
    ) {
      return <Chip className="bg-red-50 text-red-700 ring-1 ring-red-200 ring-inset">Failed</Chip>;
    }
    return <Chip className="bg-green-50 text-green-700 ring-1 ring-green-200 ring-inset">Complete</Chip>;
  }
  if (run.status === RunStatus.InQueue) {
    return <Chip className="bg-blue-50 text-blue-700 ring-1 ring-blue-200 ring-inset">In Queue</Chip>;
  }
  return <Chip className="bg-blue-50 text-blue-700 ring-1 ring-blue-200 ring-inset">Processing</Chip>;
};

const formatNumber = (num: number) => num.toLocaleString();

const getRunDurationMs = (run: MatterRun) => {
  if (!run.completedAt) return null;
  const durationMs = new Date(run.completedAt).getTime() - new Date(run.createdDate).getTime();
  return durationMs >= 0 ? durationMs : null;
};

const denseTableHeadClassName = 'h-8 px-1.5 text-xs';
const denseTableCellClassName = 'p-1.5 py-2 text-xs';

const columnHelper = createColumnHelper<MatterRun>();

export function RunLog() {
  const { matterId } = useParams();
  if (!matterId) return null;
  const { data: runs, isLoading, refetch } = useGetMatterRuns(matterId);
  const { mutateAsync: updateRunName, isLoading: isUpdatingName } = useUpdateRun();

  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [editingRun, setEditingRun] = useState<{ id: string; name: string | null } | null>(null);

  const runNameForm = useForm<RunNameFormData>({
    resolver: zodResolver(runNameSchema),
    mode: 'onTouched',
    defaultValues: { runName: '' },
  });

  const handleEditName = (run: MatterRun) => {
    setEditingRun({ id: run.id, name: run.name });
    runNameForm.reset({ runName: run.name ?? '' });
  };

  const handleSaveName = async (data: RunNameFormData) => {
    if (!editingRun) return;
    const trimmed = data.runName.trim();
    await updateRunName({ runId: editingRun.id, name: trimmed });
    toast.success('Run name updated');
    trackCustomEvent(TrackingEventsV2.RunNameEdit, { matterId });
    setEditingRun(null);
    runNameForm.reset();
    refetch();
  };

  const columns = [
    columnHelper.accessor('createdDate', {
      header: () => <span>Date</span>,
      cell: (info) => <span className="text-xs text-gray-500">{formatDate(info.getValue(), true, true)}</span>,
    }),
    columnHelper.accessor('name', {
      header: () => <span>Name</span>,
      cell: (info) => {
        const name = info.getValue();
        return (
          <div className="flex items-center gap-1.5">
            {name ? (
              <Tooltip>
                <TooltipTrigger className="max-w-32 truncate text-xs text-gray-800">{name}</TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">{name}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <span className="text-xs text-gray-400">Unnamed</span>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEditName(info.row.original);
              }}
              className="text-gray-400 transition-colors hover:text-gray-500"
            >
              <Icon className="lucide--pencil-line size-3" />
            </button>
          </div>
        );
      },
    }),
    columnHelper.accessor('type', {
      header: () => <span>Type</span>,
      cell: (info) => <p className="text-xs text-gray-800">{getRunType(info.row.original, !info.row.original.case)}</p>,
    }),
    columnHelper.display({
      id: 'status',
      header: () => <span>Status</span>,
      cell: (info) => getRunStatusChip(info.row.original),
    }),
    columnHelper.display({
      id: 'duration',
      header: () => <span>Duration</span>,
      cell: (info) => {
        const durationMs = getRunDurationMs(info.row.original);
        return durationMs !== null ? (
          <span className="text-xs whitespace-nowrap text-gray-800">{formatDuration(durationMs)}</span>
        ) : (
          <TableNullField />
        );
      },
    }),
    columnHelper.accessor('case', {
      header: () => <span>Subset</span>,
      cell: (info) => {
        const caseData = info.getValue();
        return caseData ? (
          <Tooltip>
            <TooltipTrigger className="max-w-36 truncate text-xs">{caseData.name}</TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">{caseData.name}</p>
            </TooltipContent>
          </Tooltip>
        ) : (
          <span className="text-xs text-gray-400">All Documents</span>
        );
      },
    }),
    columnHelper.accessor('createdBy.email', {
      header: () => <span>Submitted By</span>,
      cell: (info) => {
        const email = info.getValue();
        return email ? <span className="text-xs text-gray-900">{email}</span> : <TableNullField />;
      },
    }),
    columnHelper.accessor('successfulPagesCount', {
      header: () => <span>Processed</span>,
      cell: (info) => {
        const count = info.getValue();
        return count !== undefined && count > 0 ? (
          <div className="flex items-center gap-1.5 text-xs">
            <span className="font-medium text-green-700">{formatNumber(count)} pages</span>
          </div>
        ) : (
          <TableNullField />
        );
      },
    }),
    columnHelper.accessor('failedPagesCount', {
      header: () => <span>Failed</span>,
      cell: (info) => {
        const count = info.getValue();
        return count > 0 ? (
          <div className="flex items-center gap-1.5 text-xs">
            <span className="font-medium text-red-700">{formatNumber(count)} pages</span>
          </div>
        ) : (
          <TableNullField />
        );
      },
    }),
  ];

  const table = useReactTable({
    data: runs ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableSorting: false,
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  const handleRowClick = (run: MatterRun) => {
    trackCustomEvent(TrackingEventsV2.FileHistoryClickRunRow, { matterId, runId: run.id });
    setSelectedRunId(run.id);
  };

  if (isLoading) {
    return (
      <>
        <div className="my-2 flex items-center gap-1.5">
          <Icon className="lucide--info size-3 text-gray-500" />
          <p className="text-xs text-gray-600">
            Runs happen when Wexler processes your documents (e.g. extracts facts from new documents, or assesses
            relevance of facts when creating a subset).
          </p>
        </div>
        <TableComponent>
          <TableHeader>
            <TableRow>
              <TableHead className={denseTableHeadClassName}>Date</TableHead>
              <TableHead className={denseTableHeadClassName}>Name</TableHead>
              <TableHead className={denseTableHeadClassName}>Type</TableHead>
              <TableHead className={denseTableHeadClassName}>Status</TableHead>
              <TableHead className={denseTableHeadClassName}>Duration</TableHead>
              <TableHead className={denseTableHeadClassName}>Subset</TableHead>
              <TableHead className={denseTableHeadClassName}>Submitted By</TableHead>
              <TableHead className={denseTableHeadClassName}>Processed</TableHead>
              <TableHead className={denseTableHeadClassName}>Failed</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell className={denseTableCellClassName}>
                  <Skeleton className="h-4 w-28" />
                </TableCell>
                <TableCell className={denseTableCellClassName}>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell className={denseTableCellClassName}>
                  <Skeleton className="h-4 w-16" />
                </TableCell>
                <TableCell className={denseTableCellClassName}>
                  <Skeleton className="h-6 w-20" />
                </TableCell>
                <TableCell className={denseTableCellClassName}>
                  <Skeleton className="h-4 w-20" />
                </TableCell>
                <TableCell className={denseTableCellClassName}>
                  <Skeleton className="h-4 w-24" />
                </TableCell>
                <TableCell className={denseTableCellClassName}>
                  <Skeleton className="h-4 w-36" />
                </TableCell>
                <TableCell className={denseTableCellClassName}>
                  <Skeleton className="h-4 w-16" />
                </TableCell>
                <TableCell className={denseTableCellClassName}>
                  <Skeleton className="h-4 w-16" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </TableComponent>
      </>
    );
  }

  if (!runs || runs.length === 0) {
    return <div className="py-12 text-center text-sm text-gray-500">No processing runs to display</div>;
  }

  return (
    <>
      <div className="my-2 flex items-center gap-1.5">
        <Icon className="lucide--info size-3 text-gray-500" />
        <p className="text-xs text-gray-600">
          Runs happen when Wexler processes your documents (e.g. extracts facts from new documents, or assesses
          relevance of facts when creating a subset).
        </p>
      </div>
      <TableComponent>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id} className={denseTableHeadClassName}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              className="hover:bg-muted cursor-pointer"
              onClick={() => handleRowClick(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className={denseTableCellClassName}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </TableComponent>
      {runs.length > 0 && <TablePagination table={table} plural="runs" />}
      {selectedRunId && (
        <RunDetailModal
          open={!!selectedRunId}
          onOpenChange={(open) => !open && setSelectedRunId(null)}
          matterId={matterId}
          runId={selectedRunId}
          onNavigateToRun={(id) => setSelectedRunId(id)}
        />
      )}

      <Dialog
        open={!!editingRun}
        onOpenChange={(open) => {
          if (!open) {
            setEditingRun(null);
            runNameForm.reset();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Run Name</DialogTitle>
            <DialogDescription />
          </DialogHeader>

          <Form {...runNameForm}>
            <form onSubmit={runNameForm.handleSubmit(handleSaveName)}>
              <FormField
                control={runNameForm.control}
                name="runName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Run Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="e.g. Initial document batch" autoFocus />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter className="mt-6">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setEditingRun(null);
                    runNameForm.reset();
                  }}
                  disabled={isUpdatingName}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={isUpdatingName}>
                  {isUpdatingName ? (
                    <>
                      <Icon className="lucide--loader-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    'Save'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}
